"use client";

import dynamic from "next/dynamic";
import { useEffect, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

/** Every engine lives in one client chunk that is fetched only when a visitor presses Play. */
const DemoModeView = dynamic(() => import("@/features/demos/DemoModeView").then((m) => m.DemoModeView), { ssr: false, loading: () => <div className="m-6 h-40 animate-pulse rounded-2xl bg-white/5" /> });

/** Engines that render their own way out ("Skip training"); the layer hides its exit control for them. */
export const SELF_EXITING_ENGINES = new Set(["match", "auction", "mini-football-grid"]);

export const EXIT_LABEL: Record<string, string> = { en: "Exit practice", ka: "სავარჯიშოდან გასვლა", es: "Salir de la práctica", tr: "Alıştırmadan çık", ru: "Выйти из тренировки", pt: "Sair do treino", de: "Training verlassen" };

/**
 * Full-viewport layer for a practice round. The engines are full-screen
 * screens, so they open portalled to <body> (outside the app shell's scroll
 * container) with their own exit control; focus moves in and back out.
 */
/** The coin sneak peeks leave through their own header back control; the layer keeps Escape but draws no exit pill. */
export const OWN_EXIT_ENGINES = new Set(["buscaminas", "guess-the-goal", "pistas", "ultimo", "minuto", "shared-player", "name-chain", "tenable", "football-horserace", "football-codenames", "dato-falso-futbolero", "conexiones-futboleras", "football-darts-501", "daily-statSniper", "mini-trivia-mines", "mini-final-third", "mini-road-to-goal", "mini-squad-spin"]);

/** `exitControl` false = the engine renders its own way out (the training match has "Skip training"). */
/** The modal that owns the keyboard: a dialog a game opened on top of the layer (portaled elsewhere), else the layer. */
function topModal(layer: HTMLElement | null): HTMLElement | null {
  if (!layer) return null;
  const nested = Array.from(document.querySelectorAll<HTMLElement>('[aria-modal="true"]')).filter((el) => el !== layer && !layer.contains(el));
  return nested.at(-1) ?? layer;
}

export function PracticeLayer({ title, exitLabel, onExit, exitControl = true, exitButton = true, children }: { title: string; exitLabel: string; onExit: () => void; exitControl?: boolean; /** false = Escape still exits but the engine draws its own exit control. */ exitButton?: boolean; children: ReactNode }) {
  const exitRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    // Move focus into the layer: the exit control when drawn, else the dialog itself.
    (exitRef.current ?? dialogRef.current)?.focus();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
      opener?.focus?.({ preventScroll: true });
    };
  }, []);
  // Self-exiting engines bind Escape to their own skip (it records completion + analytics).
  useEffect(() => {
    if (!exitControl) return;
    // A game's own dialog on top (a collection, a result sheet) closes on Escape itself; the layer stays open.
    const onKey = (event: KeyboardEvent) => { if (event.key === "Escape" && topModal(dialogRef.current) === dialogRef.current) onExit(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [exitControl, onExit]);
  // aria-modal only describes the dialog; keep Tab/Shift+Tab inside it so the page behind stays unreachable.
  useEffect(() => {
    const onTab = (event: KeyboardEvent) => {
      const dialog = topModal(dialogRef.current);
      if (event.key !== "Tab" || !dialog) return;
      const focusable = Array.from(dialog.querySelectorAll<HTMLElement>('a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])'))
        .filter((el) => !el.hasAttribute("disabled") && el.getClientRects().length > 0);
      if (focusable.length === 0) { event.preventDefault(); dialog.focus(); return; }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (!active || !dialog.contains(active)) { event.preventDefault(); first.focus(); return; }
      if (event.shiftKey && (active === first || active === dialog)) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && active === last) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", onTab);
    return () => document.removeEventListener("keydown", onTab);
  }, []);
  if (typeof document === "undefined") return null;
  return createPortal(
    <div ref={dialogRef} tabIndex={-1} role="dialog" aria-modal="true" aria-label={title} className="fixed inset-0 z-[80] overflow-y-auto bg-surface-page-alt outline-none">
      {exitControl && exitButton && (
        <button
          ref={exitRef}
          type="button"
          onClick={onExit}
          className="fixed right-3 top-3 z-[120] inline-flex h-9 items-center gap-1 rounded-full bg-black/60 px-3 text-xs font-bold uppercase tracking-wide text-white backdrop-blur-sm hover:bg-black/80"
        >
          <X className="size-4" /> {exitLabel}
        </button>
      )}
      {children}
    </div>,
    document.body,
  );
}

/** A practice prototype (no account state) in the layer; used by the hub's ranked demo and the public game pages. */
export function PracticeDemo({ slug, title, locale, backHref, onExit, onEvent }: {
  slug: string; title: string; locale: string; backHref: string; onExit: () => void;
  onEvent?: (event: "start" | "complete" | "replay", detail?: { score?: number }) => void;
}) {
  return (
    <PracticeLayer title={title} exitLabel={EXIT_LABEL[locale] ?? EXIT_LABEL.en} onExit={onExit} exitControl={!SELF_EXITING_ENGINES.has(slug)}>
      <DemoModeView slug={slug} backHref={backHref} onExit={onExit} onEvent={onEvent} />
    </PracticeLayer>
  );
}
